import { Form, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectField } from 'src/components/Form/SelectField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import { Modal } from 'src/components/Modal/Modal';
import { PartnerDto, ProductDto } from 'src/generated-api-client';
import { ProductDetails } from 'src/pages/LoanApplicationDetailsPage/components/ProductDetails';
import { LoanApplicationsStore } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import { PartnersStore } from 'src/stores/PartnersStore/PartnersStore';
import { OptionType } from 'src/stores/ResourceStore/ResourceStore';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import { addModalRenderer, ModalRendererProps } from 'src/utils/ModalRenderer';

type AssignApplicationDialogProps = ModalRendererProps<{
    applicationId: string | number;
}>;

const AssignApplicationDialogFn = observer(
    ({ applicationId, onSuccess, ...props }: AssignApplicationDialogProps) => {
        const { t } = useTranslation();
        const [form] = Form.useForm<AssignApplicationFormValues>();
        const [selectedPartner, setSelectedPartner] = useState<PartnerDto>();
        const [selectedProduct, setSelectedProduct] = useState<ProductDto>();

        useEffect(() => {
            PartnersStore.loadList();

            return () => {
                PartnersStore.listLoader.reset();
                store.assignProductLoader.reset();
            };
        }, []);

        const productOptions = useMemo(() => {
            return selectedPartner?.products?.map<OptionType<ProductDto>>(
                (product) => {
                    return {
                        label: product.name,
                        value: product.id,
                        item: product,
                    };
                },
            );
        }, [selectedPartner]);

        const fields = FormHelper.getFieldDataItems(
            store.assignProductLoader.errorData as ConstraintViolationError,
        );

        const onFinish = async (values: AssignApplicationFormValues) => {
            await store.assignProduct(String(applicationId), values.productId!);
            if (!store.assignProductLoader.hasError) {
                notification.success({
                    message: t('App.AssignApplicationDialog.AssignSuccess'),
                });
                onSuccess?.();
            } else {
                return Promise.reject();
            }
        };

        return (
            <Modal
                form={form}
                title={t('App.AssignApplicationDialog.Title')}
                okText={t('App.AssignApplicationDialog.Assign')}
                isLoading={store.assignProductLoader.isLoading}
                formProps={{ fields }}
                onFinish={onFinish}
                {...props}
            >
                <SelectField
                    name={formNames.getPath('partnerId')}
                    label={t('App.AssignApplicationDialog.Partner')}
                    rules={[ValidationRules.required]}
                    options={PartnersStore.partnerAsOptions}
                    isLoading={PartnersStore.listLoader.isLoading}
                    selectProps={{
                        onSelect: (_, selectedItem) => {
                            setSelectedPartner(
                                (selectedItem as OptionType<PartnerDto>).item,
                            );
                            setSelectedProduct(undefined);
                        },
                    }}
                />
                {selectedPartner && (
                    <SelectField
                        name={formNames.getPath('productId')}
                        label={t('App.AssignApplicationDialog.Product')}
                        rules={[ValidationRules.required]}
                        options={productOptions}
                        selectProps={{
                            onSelect: (_, selectedItem) => {
                                setSelectedProduct(
                                    (selectedItem as OptionType<ProductDto>)
                                        .item,
                                );
                            },
                        }}
                    />
                )}
                {selectedProduct && selectedPartner && (
                    <ProductDetails
                        product={selectedProduct}
                        partner={selectedPartner}
                    />
                )}
            </Modal>
        );
    },
);

type AssignApplicationFormValues = {
    partnerId?: string;
    productId?: string;
};
const formNames = FormHelper.createNameProvider<AssignApplicationFormValues>();
const store = LoanApplicationsStore;

export const AssignApplicationDialog = addModalRenderer(
    AssignApplicationDialogFn,
);
